import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, MapPin, BadgeCheck, Check, Phone, Navigation, Download, FileText } from 'lucide-react';
import { PROJECTS, COMPANY } from '../data/site';
import { useEnquiry } from '../context/EnquiryContext';

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.7, delay },
});

export default function ProjectDetail() {
  const { id } = useParams();
  const { open } = useEnquiry();
  const project = PROJECTS.find((p) => p.id === id);

  if (!project) {
    return (
      <main className="pt-40 pb-32 min-h-screen" data-testid="project-not-found">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="font-heading font-extrabold text-4xl tracking-tighter text-ink">Project not found</h1>
          <Link to="/projects" data-testid="notfound-back-btn" className="mt-6 inline-flex items-center gap-2 text-terra text-[11px] tracking-[0.2em] font-medium">
            <ArrowLeft size={14} /> ALL PROJECTS
          </Link>
        </div>
      </main>
    );
  }

  const others = PROJECTS.filter((p) => p.id !== id);

  return (
    <main data-testid="project-detail-page">
      <section className="relative h-[62vh] min-h-[440px] overflow-hidden bg-bark">
        <img src={project.image} alt={project.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-bark/50 via-bark/20 to-bark/85" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-6 lg:px-10 pb-14">
          <Link to="/projects" data-testid="detail-back-link" className="inline-flex items-center gap-2 text-sand/70 text-[10px] tracking-[0.24em] font-medium mb-6 hover:text-terra transition-colors duration-200">
            <ArrowLeft size={13} /> ALL PROJECTS
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading font-extrabold tracking-tighter text-4xl sm:text-6xl lg:text-7xl text-sand"
            data-testid="detail-title"
          >
            {project.name}
          </motion.h1>
          <p className="mt-4 text-sand/70 text-sm flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-2"><MapPin size={14} className="text-terra" /> {project.location}</span>
            <span className="mx-2 text-sand/30">|</span>
            <span className="flex items-center gap-2"><BadgeCheck size={14} className="text-terra" /> {project.approval}</span>
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20 border-b border-clay" data-testid="detail-overview">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">PROJECT OVERVIEW</p>
          <motion.h2 {...fade(0)} className="font-heading font-bold tracking-tight text-2xl sm:text-3xl text-ink max-w-2xl leading-snug">{project.tagline}</motion.h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 border border-clay divide-x divide-clay" data-testid="detail-facts">
            {[['Status', project.status], ['Category', project.category], ['Approval', project.approval], ['Payment', project.price]].map(([k, v]) => (
              <div key={k} className="p-5">
                <p className="text-[9px] tracking-[0.24em] text-ink/40 font-medium mb-2">{k.toUpperCase()}</p>
                <p className="text-sm font-medium text-ink leading-snug">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {(project.highlights.length > 0 || project.mapEmbed) && (
        <section className="py-16 lg:py-20 border-b border-clay" data-testid="detail-highlights-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">LOCATION</p>
              <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-ink">Project highlights</h2>
              {project.mapUrl && (
                <a href={project.mapUrl} target="_blank" rel="noopener noreferrer" data-testid="detail-maps-btn"
                  className="mt-8 inline-flex items-center gap-2 border border-ink/30 text-ink text-[11px] font-medium tracking-[0.2em] px-8 py-4 hover:bg-moss hover:text-sand hover:border-moss transition-colors duration-300">
                  <MapPin size={14} /> VIEW ON GOOGLE MAPS
                </a>
              )}
            </div>
            {project.highlights.length > 0 && (
              <ul className="lg:col-span-7 border-t border-clay" data-testid="detail-highlights">
                {project.highlights.map((h) => (
                  <li key={h} className="flex items-center gap-3 py-3.5 border-b border-clay text-sm text-ink/70">
                    <Navigation size={13} className="text-terra shrink-0" /> {h}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {project.mapEmbed && (
            <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12">
              <motion.div {...fade(0)} className="border border-clay bg-stone" data-testid="detail-map-embed">
                <iframe
                  title={`${project.name} — map location`}
                  src={project.mapEmbed}
                  className="w-full h-80 lg:h-96 grayscale-[25%] contrast-[1.05]"
                  loading="lazy"
                />
              </motion.div>
            </div>
          )}
        </section>
      )}

      {project.gallery.length > 0 && (
        <section className="py-16 lg:py-20 border-b border-clay">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">PROJECT IMAGES</p>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-ink mb-12">On the ground</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="detail-gallery">
              {project.gallery.map((g, i) => (
                <motion.div key={g} {...fade((i % 2) * 0.1)} className="group overflow-hidden aspect-[4/3] bg-stone" data-testid={`gallery-img-${i}`}>
                  <img src={g} alt={`${project.name} site photo ${i + 1}`} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {project.amenities.length > 0 && (
        <section className="py-16 lg:py-20 border-b border-clay" data-testid="detail-amenities-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">AMENITIES</p>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-ink mb-10">Planned for living</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4" data-testid="detail-amenities">
              {project.amenities.map((a) => (
                <li key={a} className="flex items-center gap-3 text-sm text-ink/70">
                  <span className="w-5 h-5 bg-terra/10 text-terra flex items-center justify-center shrink-0"><Check size={12} /></span>
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {project.flyer && (
        <section className="py-16 lg:py-20 border-b border-clay bg-stone" data-testid="detail-flyer-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row sm:items-center justify-between gap-8">
            <div className="flex items-start gap-5">
              <span className="w-12 h-12 bg-terra/10 text-terra flex items-center justify-center shrink-0"><FileText size={20} /></span>
              <div>
                <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-2">PROJECT FLYER</p>
                <h2 className="font-heading font-extrabold tracking-tighter text-2xl sm:text-3xl text-ink">Take {project.name} home with you</h2>
                <p className="mt-2 text-sm text-ink/55">Full layout plan, amenities and offers — one PDF.</p>
              </div>
            </div>
            <a href={project.flyer} download data-testid="detail-download-flyer-btn"
              className="group bg-moss text-sand text-[11px] font-medium tracking-[0.2em] px-8 py-4 flex items-center gap-2 w-max hover:bg-terra transition-colors duration-300 shrink-0">
              <Download size={14} /> DOWNLOAD FLYER
            </a>
          </div>
        </section>
      )}

      <section className="py-16 lg:py-20 border-b border-clay">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="bg-moss text-sand p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" data-testid="detail-enquiry-card">
            <div>
              <p className="text-[10px] tracking-[0.28em] text-terra font-medium mb-3">BOOK YOUR PLOT</p>
              <p className="font-heading font-bold text-2xl lg:text-3xl tracking-tight leading-snug">{project.price}</p>
              <p className="mt-4 text-sm text-sand/60 leading-relaxed max-w-md">Free site visit, transparent documentation and registration support from our office.</p>
            </div>
            <div className="flex flex-col gap-3">
              <button onClick={() => open(project.name)} data-testid="detail-enquire-btn"
                className="w-full bg-terra text-white text-[11px] font-medium tracking-[0.2em] py-4 flex items-center justify-center gap-2 hover:bg-sand hover:text-ink transition-colors duration-300">
                ENQUIRE NOW <ArrowUpRight size={14} />
              </button>
              <a href={`tel:${COMPANY.phoneRaw}`} data-testid="detail-call-btn"
                className="w-full border border-sand/30 text-sand text-[11px] font-medium tracking-[0.2em] py-4 flex items-center justify-center gap-2 hover:bg-sand hover:text-ink transition-colors duration-300">
                <Phone size={13} /> CALL {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-ink mb-12">Related projects</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`related-${p.id}`} className="group">
                <div className="relative overflow-hidden aspect-[16/10] bg-stone">
                  <img src={p.image} alt={p.name} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                  <span className={`absolute top-4 left-4 text-[10px] tracking-[0.18em] font-medium px-3 py-1.5 ${p.status === 'Completed' ? 'bg-sand/90 text-ink' : 'bg-terra text-white'}`}>
                    {p.status.toUpperCase()}
                  </span>
                </div>
                <h3 className="mt-4 font-heading font-bold text-lg tracking-tight text-ink group-hover:text-terra transition-colors duration-300">{p.name}</h3>
                <p className="mt-1 text-xs text-ink/50 flex items-center gap-1.5"><MapPin size={11} className="text-terra" /> {p.location}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
